'use client'

import { Button } from '@/ui/button'
import {
  ShareNetworkIcon,
  BellIcon,
  CheckIcon,
  XIcon,
} from '@phosphor-icons/react'
import type { InboxItem } from './actions'

interface InboxItemCardProps {
  item: InboxItem
  onMarkRead: (id: string) => void
  onAct: (id: string, action: 'accepted' | 'declined' | 'dismissed') => void
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export function InboxItemCard({ item, onMarkRead, onAct }: InboxItemCardProps) {
  const payload = item.payload as Record<string, string | undefined>
  const isUnread = item.status === 'unread'

  const handleClick = () => {
    if (isUnread) onMarkRead(item.id)
  }

  const TypeIcon = item.itemType === 'share' ? ShareNetworkIcon : BellIcon

  const title =
    item.itemType === 'share'
      ? `Shared: ${payload.title || 'Entry'}`
      : (payload.message ?? 'Notification')

  const subtitle =
    item.itemType === 'share' && payload.grantorHandle
      ? `from @${payload.grantorHandle}`
      : undefined

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 rounded-lg border p-4 transition-colors ${
        isUnread ? 'bg-muted/40 border-foreground/10' : 'border-transparent'
      }`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <TypeIcon
        className={`mt-0.5 h-5 w-5 shrink-0 ${
          isUnread ? 'text-foreground' : 'text-muted-foreground'
        }`}
      />

      <div className="min-w-0 flex-1">
        <p className={`text-sm ${isUnread ? 'font-medium' : ''}`}>{title}</p>
        {subtitle && (
          <p className="text-muted-foreground text-xs">{subtitle}</p>
        )}
        <p className="text-muted-foreground mt-1 text-xs">
          {formatTimeAgo(item.createdAt)}
        </p>
      </div>

      {item.itemType === 'share' && item.status !== 'acted' && (
        <div className="flex shrink-0 gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation()
              onAct(item.id, 'accepted')
            }}
          >
            <CheckIcon className="h-3.5 w-3.5" />
            Accept
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation()
              onAct(item.id, 'dismissed')
            }}
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}
